import type { PulsarIdentity } from '@/utils/analytics/pulsar';

/**
 * What the beacon should be told for a given (previous → current) authenticated-id transition.
 * Extracted as a pure function (like `resolveMarketingConsent`) so the edges are unit-tested without
 * a store. The four outcomes:
 *   - `identify` — an id is present and it is the SAME user we last identified (or the first one);
 *   - `reidentify` — an id is present but it is a DIFFERENT user than we last identified (e.g. a
 *     support-impersonation switch with no sign-out in between). The caller must clear `_u`/`_s`
 *     BEFORE writing the new identity: `pulsarIdentify` only writes ids that are present, so a switch
 *     to a user whose `session_id` is absent would otherwise leave the previous user's `_s` cookie in
 *     place and attribute the new user's events to the old session;
 *   - `logout` on either sign-out edge — `prevUserId` (an id we identified THIS run has gone away,
 *     mid-session sign-out) or `authSettledToGuest` (the boot auth check RESOLVED to signed-out, so a
 *     previous user's persisted `_u`/`_s` on a shared/returning browser must not keep attributing).
 *     `authSettledToGuest` is passed ONLY once the check has actually run — never during the initial,
 *     unresolved guest render, so a persisted identity survives the auth-restore gap on reload;
 *   - `none` — nothing to do.
 */
export type PulsarIdentityAction =
  | { type: 'identify'; identity: PulsarIdentity }
  | { type: 'reidentify'; identity: PulsarIdentity }
  | { type: 'logout' }
  | { type: 'none' };

export function resolvePulsarIdentityAction(
  prevUserId: string | null,
  userId: string | null,
  sessionId: string | null,
  authSettledToGuest = false,
): PulsarIdentityAction {
  if (userId) {
    const identity = { user_id: userId, session_id: sessionId };
    // A switch to a DIFFERENT signed-in user with no sign-out between (support impersonation) must
    // clear the previous user's cookies first — otherwise an absent new session_id leaves the prior
    // user's `_s` attributing this user's events.
    if (prevUserId && prevUserId !== userId) return { type: 'reidentify', identity };
    return { type: 'identify', identity };
  }
  if (prevUserId || authSettledToGuest) return { type: 'logout' };
  return { type: 'none' };
}
